import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon, Link } from '@evertrue/et-components';
import EnrichmentConfidencePill from '../enrichment-confidence-pill';

/**
 * Format currency values for display in table cells. Returns a dash for null/undefined.
 */
const formatCurrency = (value) => {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Format numeric values for display in table cells. Returns a dash for null/undefined.
 */
const formatNumber = (value) => {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US').format(value);
};

const Placeholder = () => (
  <div className="real-estate-address-cell--placeholder">
    <Icon icon="home" />
  </div>
);

/**
 * Checks the Street View Metadata API to determine if imagery exists before rendering.
 * Google's Street View image endpoint always returns a valid JPEG (even a gray "no imagery" one),
 * so we use the metadata endpoint which returns { status: "OK" | "ZERO_RESULTS" }.
 */
const RealEstateThumbnail = ({ url }) => {
  const [available, setAvailable] = useState(null);

  useEffect(() => {
    if (!url) {
      setAvailable(false);
      return;
    }

    const controller = new AbortController();
    const metadataUrl = url.replace('/streetview?', '/streetview/metadata?');
    fetch(metadataUrl, { signal: controller.signal })
      .then((res) => res.json())
      .then((data) => setAvailable(data.status === 'OK'))
      .catch(() => {
        if (!controller.signal.aborted) setAvailable(false);
      });

    return () => controller.abort();
  }, [url]);

  if (available === null) return <Placeholder />;
  if (!available) return <Placeholder />;

  return <img src={url} alt="Property" className="real-estate-address-cell--thumbnail" />;
};

RealEstateThumbnail.propTypes = {
  url: PropTypes.string,
};

/**
 * Confidence column definition shared across all tables.
 * DonorSearch qualityScore (0–25) is mapped to binary High/Low — medium is intentionally excluded.
 */
const confidenceColumn = {
  field: 'confidence_score',
  headerName: 'Confidence',
  sortable: false,
  minWidth: 110,
  renderCell: (params) => <EnrichmentConfidencePill score={params.row?.confidence_score >= 17 ? 100 : 0} />,
};

/**
 * Column definitions for the Charitable giving table.
 */
export const charitableColumns = [
  {
    field: 'listed_donor',
    headerName: 'Listed Donor',
    flex: 1.5,
    sortable: true,
  },
  {
    field: 'recipient',
    headerName: 'Recipient',
    sortable: true,
    flex: 1.5,
    renderCell: (params) => {
      const { recipient, recipient_url } = params.row || {};
      if (recipient_url) {
        return (
          <Link href={recipient_url} target="_blank" rel="noopener noreferrer">
            {recipient}
          </Link>
        );
      }
      return recipient || '—';
    },
  },
  {
    field: 'recipient_location',
    headerName: 'Recipient Location',
    sortable: true,
    flex: 1,
    minWidth: 70,
  },
  {
    field: 'gift_type',
    headerName: 'Gift Type',
    sortable: true,
    minWidth: 100,
    renderCell: (params) => {
      const { gift_type, gift_type_url } = params.row || {};
      if (gift_type_url) {
        return (
          <Link href={gift_type_url} target="_blank" rel="noopener noreferrer">
            {gift_type || '—'}
          </Link>
        );
      }
      return gift_type || '—';
    },
  },
  {
    field: 'year',
    headerName: 'Year',
    minWidth: 30,
    sortable: true,
  },
  {
    field: 'low_amount',
    headerName: 'Low Amount',
    minWidth: 110,
    sortable: true,
    renderCell: (params) => formatCurrency(params.row?.low_amount),
  },
  {
    field: 'high_amount',
    headerName: 'High Amount',
    minWidth: 110,
    sortable: true,
    renderCell: (params) => formatCurrency(params.row?.high_amount),
  },
  confidenceColumn,
];

/**
 * Column definitions for the Political giving table.
 */
export const politicalColumns = [
  {
    field: 'listed_donor',
    headerName: 'Listed Donor',
    sortable: true,
    flex: 1,
    minWidth: 120,
  },
  {
    field: 'recipient',
    headerName: 'Recipient',
    sortable: true,
    flex: 1.5,
    minWidth: 150,
  },
  {
    field: 'donor_location',
    headerName: 'Donor Location',
    sortable: true,
    flex: 1,
    minWidth: 130,
  },
  {
    field: 'gift_type',
    headerName: 'Gift Type',
    sortable: true,
    minWidth: 80,
    renderCell: (params) => {
      const { gift_type, gift_type_url } = params.row || {};
      const displayValue = gift_type || 'FEC';
      if (gift_type_url) {
        return (
          <Link href={gift_type_url} target="_blank" rel="noopener noreferrer">
            {displayValue}
          </Link>
        );
      }
      return displayValue;
    },
  },
  {
    field: 'year',
    headerName: 'Year',
    minWidth: 50,
    sortable: true,
  },
  {
    field: 'low_amount',
    headerName: 'Low Amount',
    sortable: true,
    minWidth: 100,
    renderCell: (params) => formatCurrency(params.row?.low_amount),
  },
  {
    field: 'high_amount',
    headerName: 'High Amount',
    sortable: true,
    minWidth: 100,
    renderCell: (params) => formatCurrency(params.row?.high_amount),
  },
  {
    field: 'category',
    headerName: 'Category',
    sortable: true,
    minWidth: 90,
  },
  confidenceColumn,
];

/**
 * Column definitions for the Crowdfunding table.
 */
export const crowdfundingColumns = [
  {
    field: 'listed_donor',
    headerName: 'Listed Donor',
    sortable: true,
    flex: 1,
    minWidth: 120,
  },
  {
    field: 'recipient',
    headerName: 'Recipient',
    sortable: true,
    flex: 1.5,
    minWidth: 150,
    renderCell: (params) => {
      const { recipient, recipient_url } = params.row || {};
      if (recipient_url) {
        return (
          <Link href={recipient_url} target="_blank" rel="noopener noreferrer">
            {recipient}
          </Link>
        );
      }
      return recipient || '—';
    },
  },
  {
    field: 'donor_location',
    headerName: 'Donor Location',
    sortable: true,
    flex: 1,
    minWidth: 140,
  },
  {
    field: 'year',
    headerName: 'Year',
    sortable: true,
    minWidth: 50,
  },
  {
    field: 'low_amount',
    headerName: 'Low Amount',
    sortable: true,

    minWidth: 110,
    renderCell: (params) => formatCurrency(params.row?.low_amount),
  },
  {
    field: 'high_amount',
    headerName: 'High Amount',
    sortable: true,

    minWidth: 110,
    renderCell: (params) => formatCurrency(params.row?.high_amount),
  },
  confidenceColumn,
];

/**
 * Column definitions for the Real Estate table.
 */
export const realEstateColumns = [
  {
    field: 'name',
    headerName: 'Name',
    sortable: true,

    flex: 1,
    minWidth: 120,
  },
  {
    field: 'address',
    headerName: 'Address',
    sortable: false,

    flex: 2,
    minWidth: 280,
    renderCell: (params) => {
      const { address_street, address_city_state, thumbnail_url } = params.row || {};
      return (
        <div className="real-estate-address-cell">
          <RealEstateThumbnail url={thumbnail_url} />
          <div className="real-estate-address-cell--text">
            <div className="real-estate-address-cell--street">{address_street || '—'}</div>
            <div className="real-estate-address-cell--city">{address_city_state}</div>
          </div>
        </div>
      );
    },
  },
  {
    field: 'property_type',
    sortable: true,

    headerName: 'Type',
    minWidth: 150,
  },
  {
    field: 'estimated_value',
    headerName: 'Estimated Value',
    sortable: true,

    minWidth: 140,
    renderCell: (params) => formatCurrency(params.row?.estimated_value),
  },
  confidenceColumn,
];

/**
 * Column definitions for the SEC Holdings table.
 */
export const secColumns = [
  {
    field: 'name',
    headerName: 'Name',
    sortable: true,

    flex: 1,
    minWidth: 140,
  },
  {
    field: 'company',
    headerName: 'Company',
    sortable: true,
    flex: 1,
    minWidth: 120,
  },
  {
    field: 'total_shares_held',
    headerName: 'Total Shares Held',
    sortable: true,
    minWidth: 140,
    renderCell: (params) => formatNumber(params.row?.total_shares_held),
  },
  {
    field: 'market_value',
    headerName: 'Market Value',
    sortable: true,
    minWidth: 130,
    renderCell: (params) => formatCurrency(params.row?.market_value),
  },
  {
    field: 'total_shares_sold',
    headerName: 'Total Shares Sold',
    sortable: true,
    minWidth: 140,
    renderCell: (params) => formatNumber(params.row?.total_shares_sold),
  },
  confidenceColumn,
];
